import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import {
  AnalyticsService,
  PriceService,
  ReqService,
  ScrollService,
  SearchStateService,
} from '@core/services';
import { CurrencyCode } from '../../../../shared/enums/currency-code.enum';
import { ModifySearchComponent } from '../../../../commons/modify-search/modify-search.component';
import { AmRewardsPricingComponent } from '@am/rewards-pricing';
import { PriceComponent } from '../../../../commons/price/price.component';
import { MbAlert, MbAdvice, MbPopoverTrigger, MbButton } from '@am/moebius';
import { DecimalPipe } from '@angular/common';

@Component({
    selector: 'best-room-pricebox',
    templateUrl: './best-room-pricebox.component.html',
    styleUrls: ['./best-room-pricebox.component.scss'],
    standalone: true,
    imports: [
        MbAlert,
        MbAdvice,
        MbPopoverTrigger,
        PriceComponent,
        AmRewardsPricingComponent,
        MbButton,
        ModifySearchComponent,
        DecimalPipe,
    ],
})
export class BestRoomPriceboxComponent implements OnInit, OnChanges {
  @Input()
  bestAvailable: any;

  @Input() set landing(isLanding: boolean) {
    this.isLanding = isLanding;
  }

  @Input()
  hotelId: string;
  @Input()
  showAmountTotal: boolean;
  @Input()
  city: any;
  @Input()
  country: any;
  @Input()
  hotelName: string;
  @Input()
  hotelStars: number;
  @Input()
  isDisney: boolean;

  public isLanding: boolean;
  public resultPriceDifference: any;
  public loading: boolean;
  public isAvailable: boolean;
  public quantityNights: number;
  public quantityGuests: number;
  public quantityRooms: number;
  public superclubPrice: number;
  public isAlliance: boolean;
  public commission: any;
  public priceTotalToShow: number;
  public userCountry: string;
  public priceTotalWithoutDiscountToShow: number;
  public hotelStaticSearch: { name: string; stars: number };
  public priceByValue: any;
  public selectedPaymentMethod: string;
  public isArgentinian: boolean;
  public btnLabel = 'Reservar';
  public hasBestAvailable = false;

  constructor(
    private searchStateService: SearchStateService,
    private reqService: ReqService,
    private analyticsService: AnalyticsService,
    private scrollService: ScrollService,
    private priceService: PriceService,
  ) {}

  ngOnInit() {
    if (this.bestAvailable) {
      this.hasBestAvailable = !(Object.keys(this.bestAvailable).length === 0);
    }

    this.userCountry = this.reqService.getCountry();
    this.quantityNights = this.priceService.totalNights;
    this.quantityRooms = this.priceService.totalRooms;
    this.priceByValue = this.searchStateService.getPriceBy();

    if (!this.isLanding) {
      const decodedRooms = this.searchStateService.getDecodedRooms();
      this.loading = false;
      this.isAvailable = true;
      this.quantityGuests = decodedRooms.total;
      if (this.hasBestAvailable) {
        this.setBestAvailable();
        if (this.resultPriceDifference !== 0) {
          this.trackPriceChange();
        }
      }
      this.isAlliance = this.reqService.isAlliance();
      this.superclubPrice = Math.ceil(this.priceTotalToShow);
      // Push event for MKT
      dataLayer.push({
        event: 'trackDataHTL',
        destination: this.city.name,
        startDate: this.searchStateService.getDecodedDate().from,
        endDate: this.searchStateService.getDecodedDate().to,
        passengers: this.searchStateService.getDecodedRooms().total,
        idProduct: this.hotelId,
        priceTotal: this.hasBestAvailable ? Math.ceil(this.priceTotalToShow) : 0,
        name: this.searchStateService.getEntityName(),
        country: this.country.name,
      });
    }
    this.hotelStaticSearch = {
      name: this.hotelName,
      stars: this.hotelStars,
    };
    this.selectedPaymentMethod = this.searchStateService.getPaymentMethod();
    this.isArgentinian = ['1123215', 'NEW8'].includes(this.country.id);
    this.btnLabel = this.getBtnLabel();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.bestAvailable && !changes.bestAvailable.isFirstChange()) {
      if (this.hasBestAvailable) {
        this.setBestAvailable();
      }
    }
  }

  /**
   * Tells whether the rate of the best available room is cheaper than the
   * rate seen on the search section
   * @returns {Boolean}
   */
  public isCheaperFromBest() {
    return this.resultPriceDifference < 0;
  }

  private setBestAvailable() {
    this.bestAvailable = this.getRate(this.bestAvailable);
    this.resultPriceDifference = this.bestAvailable.price.x_difference;
    this.commission =
      this.bestAvailable.price.commission.value - this.bestAvailable.price.commission.tax;

    if (this.bestAvailablePrice) {
      this.priceTotalWithoutDiscountToShow = this.priceService.getPriceWithoutDiscount(
        this.bestAvailable,
        this.priceByValue,
        this.showAmountTotal,
      );
      this.priceTotalToShow = this.priceService.getPrice(
        this.bestAvailable,
        this.priceByValue,
        this.showAmountTotal,
      );
    }
  }

  private trackPriceChange() {
    this.analyticsService.trackEvent({
      section: 'Detail',
      action: 'Modified Rate',
      label: this.resultPriceDifference > 0 ? 'More Expensive Rate' : 'Cheaper Rate',
      nonInteraction: true,
    });
  }

  public trackShowRooms() {
    this.analyticsService.trackEvent({
      section: 'Detail',
      action: 'Show Rooms',
      label: 'Click',
    });
    this.scrollService.scrollToElement('#more-rooms', '#room-options');
  }

  /**
   * Get default rate check is disney or
   * @return rate
   */
  private getRate(bestAvailable) {
    if (this.isDisney) {
      return bestAvailable.disney_meal_plans[0].rates[0];
    }
    return bestAvailable.rates[0];
  }

  public showAmountPerNight(rate) {
    return {
      without_promo: this.priceService.getPriceWithoutDiscount(
        rate,
        'NIGHT',
        this.showAmountTotal,
      ),
      total: this.priceService.getPrice(rate, 'NIGHT', this.showAmountTotal),
    };
  }

  private getBtnLabel(): string {
    if (this.bestAvailable === null || this.bestAvailable === undefined) {
      return '';
    }
    const currencyBestAvailable = this.bestAvailable.price
      ? this.bestAvailable.price.currency.code
      : CurrencyCode.ARS;
    const isExpert = this.reqService.isExperts();

    if (currencyBestAvailable.toUpperCase() === CurrencyCode.USD || isExpert) {
      return 'Seleccionar';
    }
    return 'Reservar';
  }

  get bestAvailablePrice() {
    return this.bestAvailable.price || null;
  }
}
